import axios from 'axios';
import dayjs from 'dayjs';
import { message } from 'antd';

// 상수 정의
export const TOKEN_SAVE_NAME = 'app_token_data';
export const TOKEN_RELOAD_MESSAGE_NAME = 'onTokenRefreshed';
export const ASKeyToken = 'token';

// 환경별 API HOST 자동 분기
export const getAPIHost = () => {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:4300/v1';
  }
  // 배포 환경은 .env나 빌드 시 NODE_ENV/BETA로 구분 가능
  if (window.location.hostname.includes('beta')) {
    return 'https://beta-bf-api.humanb.kr/v1';
  }
  // 그 외는 prod
  return 'https://bf-api.humanb.kr/v1';
};

// 기타 API HOST 관련 함수들
export const getWebURL = () => getAPIHost();
export const getFileHost = () => `${getAPIHost()}/file`;
export const getImages = ({ photoPath, photoKey, photoExt }) =>
  `https://cdn.humanb.kr/${photoPath}/${photoKey}.${photoExt}`;

// 토큰 반환 함수
export const getToken = (setCookie) => {
  const store = localStorage.getItem(TOKEN_SAVE_NAME);
  if (store) {
    const token = JSON.parse(store);
    setCookie && setCookie('token', JSON.stringify(token));
    let access_token = token.access_token;
    // 만료 시 refresh_token 반환
    if ((token.expires_at || 0) < dayjs().unix()) {
      access_token = token.refresh_token;
    }
    return access_token;
  }
  return null;
};

// 파일 업로드용 토큰
export const getFileUploadToken = () => {
  const store = localStorage.getItem(TOKEN_SAVE_NAME);
  if (store) {
    const token = JSON.parse(store);
    let access_token = token.access_token;
    if ((token.expires_at || 0) < dayjs().unix()) {
      access_token = token.refresh_token;
    }
    return access_token;
  }
  return null;
};

// Axios 인스턴스
export const restApi = axios.create({
  baseURL: getAPIHost(),
});

// 요청 인터셉터 (토큰 자동 주입)
restApi.interceptors.request.use((request) => {
  const store = localStorage.getItem(TOKEN_SAVE_NAME);
  if (store) {
    const token = JSON.parse(store);
    let access_token = token.access_token;
    // 만료 시 refresh_token 사용
    if ((token.expires_at || 0) < dayjs().unix()) {
      access_token = token.refresh_token;
    }
    request.headers.authorization = `Bearer ${access_token}`;
  }
  return request;
});

// 응답 인터셉터 (토큰 만료 시 재발급 시도)
restApi.interceptors.response.use(
  async (response) => {
    // 1. 토큰 만료 코드 처리
    if (response?.data?.code === '40300' || response?.data?.message === 'TOKEN_EXPIRED') {
      const { config } = response;
      if (config._retry) {
        // 2. 이미 재시도했으면 강제 로그아웃(무한루프 방지)
        localStorage.setItem(TOKEN_SAVE_NAME, '');
        message_error('세션이 만료되었습니다. 다시 로그인 해주세요.');
        window.location.href = '/login';
        return Promise.reject(new Error("토큰 만료, 재로그인 필요"));
      }
      config._retry = true;

      // 3. refresh token으로 재발급 시도
      const store = localStorage.getItem(TOKEN_SAVE_NAME);
      const refresh_token = store && JSON.parse(store).refresh_token;
      const ip = '0.0.0.0';
      if (refresh_token) {
        try {
          const res = await axios.get(
            `${getAPIHost()}/account/token?refreshToken=${refresh_token}&ipAddress=${ip}`
          );
          if (res.data.message === 'OK' && res.data.payload) {
            localStorage.setItem(TOKEN_SAVE_NAME, JSON.stringify(res.data.payload));
            config.headers.authorization = `Bearer ${res.data.payload.access_token}`;
            return axios(config); // 재시도 (1회만)
          } else {
            // 4. 재발급 실패 (refresh token도 만료 등)
            localStorage.setItem(TOKEN_SAVE_NAME, '');
            message_error('세션이 만료되었습니다. 다시 로그인 해주세요.');
            window.location.href = '/login';
            return Promise.reject(new Error("토큰 만료, 재로그인 필요"));
          }
        } catch (err) {
          // 5. 네트워크 에러 등
          localStorage.setItem(TOKEN_SAVE_NAME, '');
          message_error('세션이 만료되었습니다. 다시 로그인 해주세요.');
          window.location.href = '/login';
          return Promise.reject(new Error("토큰 만료, 재로그인 필요"));
        }
      } else {
        // 6. refresh_token이 없을 때
        localStorage.setItem(TOKEN_SAVE_NAME, '');
        message_error('세션이 만료되었습니다. 다시 로그인 해주세요.');
        window.location.href = '/login';
        return Promise.reject(new Error("토큰 만료, 재로그인 필요"));
      }
    }
    return response;
  },
  (error) => {
    if (error?.response?.status === 403) {
      localStorage.setItem(TOKEN_SAVE_NAME, '');
      message_error('세션이 만료되었습니다. 다시 로그인 해주세요.');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 인증용 axios 인스턴스 (기본 템플릿)
export const authApi = axios.create({
  baseURL: getAPIHost(),
  auth: {
    username: '[oauth_username]',
    password: '[oauth_password]',
  },
});

// 토큰 저장 함수
export const setToken = (tokens, setCookie) => {
  if (!tokens || !tokens.access_token) {
    localStorage.setItem(TOKEN_SAVE_NAME, '');
    return '';
  }
  const now = dayjs().unix() - 1;
  const {
    access_token,
    refresh_token,
    access_token_start_at,
    expires_in,
    expires_at,
  } = tokens;
  const store_data = {
    access_token,
    refresh_token,
    access_token_start_at: access_token_start_at || now,
    expires_at: expires_at || now + (expires_in || 3600),
  };
  localStorage.setItem(TOKEN_SAVE_NAME, JSON.stringify(store_data));
  setCookie && setCookie('token', JSON.stringify(store_data));
  if (window.opener) {
    window.opener.postMessage(TOKEN_RELOAD_MESSAGE_NAME, '*');
  }
  return store_data;
};

// 메시지 함수 (Ant Design)
export const message_info = (props) =>
  message.info({ content: props, duration: 5, key: 'cy', onClick: () => message.destroy('cy') });
export const message_success = (props) =>
  message.success({ content: props, duration: 5, key: 'cy', onClick: () => message.destroy('cy') });
export const message_error = (props) =>
  message.error({ content: props, duration: 5, key: 'cy', onClick: () => message.destroy('cy') });
export const message_warning = (props) =>
  message.warning({ content: props, duration: 5, key: 'cy', onClick: () => message.destroy('cy') });
export const message_loading = (props) =>
  message.loading({ content: props, duration: 5, key: 'cy', onClick: () => message.destroy('cy') });
